import { useHandleError } from "@/hooks/useHandleError";
import { useEntryStore } from "@/store/entry.store";
import { useSoccerRound } from "@roman/shared/hooks/soccer/useSoccerRound";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useImmer } from "use-immer";
import { SelectPlayers } from "./SelectPlayers";

export const SelectPlayersPage = () => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const entryDetails = useEntryStore((state) => state.entryDetails);
  const setRound = useEntryStore((state) => state.setRound);
  const round = useEntryStore((state) => state.round);
  const resetAll = useEntryStore((state) => state.resetAll);
  const setPlayerIds = useEntryStore((state) => state.setPlayerIds);

  const [selectedPlayers, setSelectedPlayers] = useImmer<string[]>([]);

  const { roundId } = useParams();
  if (!roundId) {
    throw new Error("Invalid roundId in SelectPlayers");
  }

  const { data: roundData } = useSoccerRound({
    roundId,
    lang: "ja",
    swrOptions: {
      onError: useHandleError("ラウンドの取得に失敗しました"),
    },
  });

  const handleSubmit = () => {
    if (!isSubmitButtonEnable) {
      return;
    }
    setPlayerIds(selectedPlayers);

    navigate(`/rounds/${roundId}/select-captain`);
  };

  const isSubmitButtonEnable = useMemo(
    () => selectedPlayers.length > 0 && selectedPlayers.every((player) => !!player),
    [selectedPlayers],
  );

  useEffect(() => {
    if (!roundData || roundData.round.id === round?.id) {
      return;
    }
    if (roundData.round.id !== round?.id) {
      resetAll();
      setRound(roundData.round);
    }
  }, [roundData, round, setRound, resetAll]);

  useEffect(() => {
    if (!round) {
      return;
    }
    const newSelectedPlayers = entryDetails.playerIds.length
      ? entryDetails.playerIds
      : Array(round.players.homePlayers.length).fill("");

    setSelectedPlayers((draft) => {
      if (draft.length === newSelectedPlayers.length && draft.every((p, i) => p === newSelectedPlayers[i])) {
        return;
      }

      return newSelectedPlayers;
    });

    let endPlayerIndex = 0;
    for (let i = 0; i < newSelectedPlayers.length; i++) {
      if (newSelectedPlayers[i]) {
        endPlayerIndex = i;
      }
    }

    if (newSelectedPlayers[endPlayerIndex] && newSelectedPlayers[endPlayerIndex] !== "") {
      endPlayerIndex += 1;

      if (endPlayerIndex >= newSelectedPlayers.length - 1) {
        endPlayerIndex = newSelectedPlayers.length - 1;
      }
    }

    setCurrentIndex(endPlayerIndex);
  }, [round, entryDetails.playerIds, setSelectedPlayers]);

  return (
    <div className="w-full h-full">
      {round && (
        <SelectPlayers
          round={round}
          selectedPlayers={selectedPlayers}
          setSelectedPlayers={setSelectedPlayers}
          setPlayerRowIndex={setCurrentIndex}
          setCurrentIndex={setCurrentIndex}
          handleSubmit={handleSubmit}
          isSubmitButtonEnable={isSubmitButtonEnable}
          currentIndex={currentIndex}
        />
      )}
    </div>
  );
};
